import { useEffect, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { AlertTriangle, CheckCircle2, RotateCcw, XCircle, Bell } from "lucide-react";
import { fetchNotifications } from "@/lib/api";

const iconFor = (kind) => {
  if (kind === "verified") return { Icon: CheckCircle2, color: "text-green-700", bg: "bg-green-50", border: "border-green-200" };
  if (kind === "reject") return { Icon: XCircle, color: "text-red-700", bg: "bg-red-50", border: "border-red-200" };
  if (kind === "reupload") return { Icon: RotateCcw, color: "text-orange-700", bg: "bg-orange-50", border: "border-orange-200" };
  return { Icon: AlertTriangle, color: "text-amber-700", bg: "bg-amber-50", border: "border-amber-200" };
};

export const NotificationBanner = ({ applicationNumber }) => {
  const [data, setData] = useState(null);
  const [expanded, setExpanded] = useState(false);

  useEffect(() => {
    if (!applicationNumber) return;
    let alive = true;
    const load = async () => {
      const d = await fetchNotifications(applicationNumber);
      if (alive) setData(d);
    };
    load();
    const iv = setInterval(load, 20000);
    return () => { alive = false; clearInterval(iv); };
  }, [applicationNumber]);

  if (!data) return null;
  const items = data.items || [];
  if (items.length === 0) return null;
  const latest = items[0];
  const { Icon, color, bg, border } = iconFor(latest.kind);

  return (
    <motion.div
      initial={{ opacity: 0, y: -6 }}
      animate={{ opacity: 1, y: 0 }}
      className={`mu-card p-4 md:p-5 ${bg} ${border}`}
      data-testid="candidate-notification-banner"
    >
      <div className="flex items-start gap-3">
        <div className={`h-9 w-9 rounded-xl bg-white border ${border} flex items-center justify-center shrink-0`}>
          <Icon className={`h-4 w-4 ${color}`} />
        </div>
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2">
            <div className="text-sm font-semibold text-[#111]">{latest.message}</div>
            {items.length > 0 && (
              <span className="inline-flex items-center gap-1 text-[10px] font-bold uppercase tracking-widest bg-white text-[#B60F2A] px-2 py-0.5 rounded-full border border-[#FBE7EA]">
                <Bell className="h-3 w-3" /> {items.length} total
              </span>
            )}
          </div>
          {latest.reason && (
            <div className="text-xs text-[#6B7280] mt-1">
              <span className="font-semibold text-[#111]">Reason: </span>{latest.reason}
            </div>
          )}
          {items.length > 1 && (
            <button
              onClick={() => setExpanded((v) => !v)}
              className="text-xs text-[#B60F2A] hover:underline mt-2"
              data-testid="btn-toggle-notifications"
            >
              {expanded ? "Hide history" : `Show all activity (${items.length})`}
            </button>
          )}
          <AnimatePresence>
            {expanded && (
              <motion.ul
                initial={{ opacity: 0, height: 0 }}
                animate={{ opacity: 1, height: "auto" }}
                exit={{ opacity: 0, height: 0 }}
                className="mt-3 space-y-2 border-t border-white/60 pt-3"
              >
                {items.map((it, i) => {
                  const { Icon: I2, color: c2 } = iconFor(it.kind);
                  return (
                    <li key={i} className="flex items-start gap-2 text-xs">
                      <I2 className={`h-3.5 w-3.5 ${c2} mt-0.5`} />
                      <div>
                        <div className="text-[#111] font-medium">{it.message}</div>
                        {it.reason && <div className="text-[#6B7280]">{it.reason}</div>}
                        {it.at && <div className="text-[10px] text-[#9CA3AF]">{new Date(it.at).toLocaleString()}</div>}
                      </div>
                    </li>
                  );
                })}
              </motion.ul>
            )}
          </AnimatePresence>
        </div>
      </div>
    </motion.div>
  );
};
